import React from "react";

/** One signer: what the old table showed as a row, with its parts named. */
export type SignerCard = {
  name: React.ReactNode;
  date: React.ReactNode;
  status: React.ReactNode;
  /** Why the signer declined; shown as text under the name, not in a tooltip. */
  declineReason?: string;
  /** The signed version of the document, when there is one. */
  file?: React.ReactNode;
  /** Sign, decline, cancel and the like, as loose buttons: the card spaces and wraps them. */
  actions?: React.ReactNode;
};

/**
 * The signers of a document as a list of cards. The page keeps them in a
 * narrow column, where a table pushed its action buttons out of view.
 *
 * The buttons of one signer sit in a row that wraps. On a phone the gap is
 * wide enough that «decline» is not tapped by mistake instead of «sign».
 * Their finger-sized height comes from the phone and touch rules of index.css.
 */
const SignerCards: React.FC<{ signers: SignerCard[] }> = ({ signers }) => (
  <ul className="m-0 list-none divide-y divide-gray-200 p-0">
    {signers.map((signer, index) => (
      <li key={index} className="py-3 first:pt-0 last:pb-0">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <span className="min-w-0 break-words text-sm font-medium">
            {signer.name}
          </span>
          {signer.status}
        </div>
        <div className="mt-1 text-sm text-gray-600">{signer.date}</div>
        {signer.declineReason && (
          <p className="m-0 mt-1 break-words text-sm text-gray-600">
            {signer.declineReason}
          </p>
        )}
        {signer.file && <div className="mt-1">{signer.file}</div>}
        {signer.actions && (
          <div
            data-testid="signer-actions"
            className="mt-2 flex flex-wrap items-center gap-2 max-md:gap-4"
          >
            {signer.actions}
          </div>
        )}
      </li>
    ))}
  </ul>
);

export default SignerCards;
